import React from "react";
import { useTranslation } from "react-i18next";
import NavigationButton from "./NavigationButton.js";
import { getLessonProgress, getPrevLessonRoute } from "pages/claudeDevCourse/devLessonsRegistry.js";
import { getV2LessonProgress, getPrevV2LessonRoute } from "pages/claudeDevV2Course/devV2LessonsRegistry.js";
import { getV3LessonProgress, getPrevV3LessonRoute } from "pages/claudeDevV3Course/devV3LessonsRegistry.js";
import { getV4LessonProgress, getPrevV4LessonRoute } from "pages/claudeDevV4Course/devV4LessonsRegistry.js";
import "./LessonFooterNav.css";

/**
 * "Module M · Lesson X of Y" for a route from ANY course.
 *
 * This footer is shared by every Claude course, but each course keeps its own
 * registry and its own progress helper, and each helper matches only its own route
 * prefix — CD01's tests /^CD_M/ and returns null for anything else. Calling CD01's
 * helper unconditionally is why the label silently rendered nothing on every CD02
 * lesson: no error, just a missing line. Ask each registry in turn instead, so a
 * course is only ever missing a label if its helper isn't wired up here.
 */
const lessonProgressFor = (route) =>
    getLessonProgress(route) || getV2LessonProgress(route) || getV3LessonProgress(route) ||
    getV4LessonProgress(route);

/** Previous lesson's route for a route from ANY course — same per-registry lookup. */
const prevLessonFor = (route) =>
    getPrevLessonRoute(route) || getPrevV2LessonRoute(route) || getPrevV3LessonRoute(route) ||
    getPrevV4LessonRoute(route);

/**
 * Where Back goes on the FIRST lesson of a course, where there is no previous
 * lesson: that course's table of contents, which is the page the student came
 * from. Falls through to the course list for anything unrecognised.
 */
const tableOfContentsFor = (route) => {
    if (/^V4_M/.test(route || "")) return "claudedev-v4-table";
    if (/^V3_M/.test(route || "")) return "claudedev-v3-table";
    if (/^V2_M/.test(route || "")) return "claudedev-v2-table";
    if (/^CD_M/.test(route || "")) return "claudedev-table";
    return null;
};

/**
 * LessonFooterNav — the full-width footer navigation for a lesson page.
 *
 * Three equal slots: Back (quiet, left) · progress (centered) · Next (primary,
 * right, shown only once the lesson's exercise is complete). Replaces the old
 * per-page `<Container className="container-lg"><Row>…</Row></Container>` block so
 * the buttons align with the full-bleed lesson panes above instead of floating
 * inside a narrow centered container.
 *
 * Props:
 *   route     — this lesson's route (e.g. "CD_M1_L1", "V2_M3_L5", "V3_M3_L1");
 *               drives the progress label, resolved by lessonProgressFor above
 *   nextRoute — route of the next lesson (or null on the last one)
 *   showNext  — reveal Next (typically the lesson's completion flag)
 *   backPage  — explicit override for Back; normally left unset so Back walks
 *               back through the course (see backTarget below)
 */
const LessonFooterNav = ({ route, nextRoute, showNext, backPage }) => {
    const { t } = useTranslation();
    const progress = lessonProgressFor(route);

    // Back is the mirror of Next: it steps to the PREVIOUS lesson. It used to
    // always land on /courses, which dropped the student out of the course
    // entirely — and the navbar already has a Courses link for that. Only the
    // first lesson of a course has nowhere back to step, and there Back returns
    // to that course's table of contents.
    const backTarget = backPage || prevLessonFor(route) || tableOfContentsFor(route) || "courses";

    return (
        <section className="lesson-footer-nav">
            <div className="lesson-footer-nav__inner">
                <div className="lesson-footer-nav__slot lesson-footer-nav__slot--start">
                    <NavigationButton direction="back" page={backTarget} />
                </div>
                <div className="lesson-footer-nav__slot lesson-footer-nav__slot--center">
                    {/* Portal target for the admin/tester "Mark lesson complete"
                        shortcut (rendered by PromptLabHubComponent), sitting just
                        above the progress label. Empty for everyone else. */}
                    <div id="lesson-footer-tester-slot" className="lesson-footer-nav__tester-slot" />
                    {progress && (
                        <span className="lesson-footer-nav__progress">
                            {t("promptLab.lessonProgress", {
                                module: progress.moduleNum,
                                lesson: progress.lessonInModule,
                                total: progress.totalInModule,
                            })}
                        </span>
                    )}
                </div>
                <div className="lesson-footer-nav__slot lesson-footer-nav__slot--end">
                    {showNext && nextRoute && <NavigationButton direction="next" page={nextRoute} />}
                </div>
            </div>
        </section>
    );
};

export default LessonFooterNav;
